import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// McLaren F1 (1992) — the low, rounded, mid-engine road supercar at true scale
// (1 unit = 1 metre): length ~4.29, width 1.82, height 1.14 (very LOW),
// wheelbase 2.72 (front axle +1.30, rear -1.42), ~0.32 m wheels. Nose is +Z.
// Authored for the `cars` showcase batch.
//
// The silhouette is one continuous LOW, WIDE TEARDROP, smoother and rounder
// than an F40 and with NO big wing: a low pointed nose with slim headlamps -> a
// hard-raked windscreen -> a SMALL, low, rounded central canopy (the F1's
// central driving position) set into the middle of the teardrop -> a low
// rounded engine deck sloping in one unbroken line down to a short tail — never
// a flat deck, never a tall cab. The car reads strikingly LOW AND WIDE: the
// canopy barely rises above the shoulders and the rear haunches flare wide,
// rounded, well OUTBOARD of the greenhouse over a wide rear track, so every
// square view is clearly wider than tall. The haunches are fed by big flank
// intake scoops ahead of the rear wheels; the engine cover carries mesh
// intakes, louvres and a gold engine-bay hint under the rear glass (a nod to
// the F1's gold-foil bay); the only rear aero is a thin deployable spoiler lip.
// Body panels carry the "paint" recolour slot (McLaren Orange); `drive` spins
// the wheels and `hazards` blinks four indicators.

const PAINT = "#e2761f"; // McLaren Orange — the "paint" slot default
const TRIM = "#141518"; // gloss black (louvres, pillars, splitter, spoiler lip)
const CLAD = "#202225"; // lower valances, sills, diffuser
const WELL = "#141518"; // wheel-well discs
const GLASS = "#1b2129"; // smoked side / rear glazing
const SCREEN = "#25313c"; // windscreen (a shade lighter than the tint)
const CABIN = "#0e0f12"; // interior block behind the glass
const MESH = "#0d0e10"; // engine grille + intake mesh (near-black)
const GOLD = "#c8a24a"; // gold-foil engine bay hint under the rear glass
const SPOKE = "#d7dce1"; // bright multi-spoke alloys
const DISH = "#101114"; // dark wheel dish behind the spokes
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const TAIL_RED = "#c41726"; // round tail lamps
const CHROME = "#ccd1d6";
const EXHAUST = "#2b2e33"; // exhaust tip metal (dark gunmetal)
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres). The car is LOW and WIDE: the belt line sits at
// ~0.58 and the little canopy peaks at only ~0.93, while the body runs ~1.72
// wide and the rounded rear haunches flare to a full ~1.84 over a wider rear
// track, so every square view reads clearly wider than tall. The greenhouse is
// authored a touch low so the perspective preview camera (which exaggerates
// height) still reads it as low and hunkered, not a cab.
const WHEEL_R = 0.32;
const AXLE_F = 1.3;
const AXLE_R = -1.42; // wheelbase 2.72
const WHEEL_X_F = 0.78; // half track, front
const WHEEL_X_R = 0.8; // half track, rear — wider than the front, wheels planted
const TYRE_W_F = 0.22;
const TYRE_W_R = 0.3;

const SCREEN_RAKE = 1.0; // hard-raked, low F1 windscreen (top toward the tail)
const HOOD_SLOPE = 0.12; // bonnet lid diving to the low pointed nose

// The unbroken descending deck: three tilted boxes whose top faces meet on the
// shared breakpoints (z, y_top): (-0.22, 0.925) -> (-0.88, 0.74) ->
// (-1.50, 0.64) -> (-2.00, 0.58). Each box centre is placed so its top face
// lies exactly on the line, so the canopy peak falls to the tail in ONE low line.
const SAIL_ANGLE = 0.273; // rear-window backlight dropping onto the engine deck
const DECK1_ANGLE = 0.16; // engine cover falling toward the tail
const DECK2_ANGLE = 0.119; // tail deck, the last shallow fall

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** A painted rounded mass: a unit sphere scaled per axis, never rotated
 *  (rotation applies after scale and would shear a non-uniform blob). Used for
 *  the rounded fender crowns and the wide rounded haunch shoulders that make
 *  the F1 read as a smooth low teardrop rather than a slab. */
function blob(scale: Vec3, position: Vec3): Part {
  return { geometry: new THREE.SphereGeometry(1, 16, 12), color: PAINT, position, scale, slot: "paint" };
}

/** One multi-spoke alloy over a low-profile tyre: tyre, brake disc, dark dish,
 *  five bright spokes proud of the tyre face, chrome centre cap. Every part
 *  shares `name` so the `drive` track spins them as one node about the axle.
 *  `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, halfTrack: number, tyreW: number, z: number, name: string): Part[] {
  const y = WHEEL_R;
  const outer = halfTrack + tyreW / 2; // tyre outer face — stays proud of the well disc
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, tyreW, 18, TIRE, [side * halfTrack, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.13, 0.13, 0.03, 14, BRAKE, [side * (halfTrack - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.2, 0.2, 0.06, 18, DISH, [side * (outer - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.05, 0.36, 0.085, SPOKE, [side * (outer - 0.02), y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.05, 0.05, 0.028, 12, CHROME, [side * (outer - 0.002), y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side (the arch "cut")
 *  plus a body-coloured half-torus brow over the tyre. The [0, pi/2, 0] yaw
 *  puts the 0..pi arc exactly over the top half. */
function arch(side: 1 | -1, z: number, discX: number, discR: number, browR: number, browTube: number): Part[] {
  return [
    cyl(discR, discR, 0.04, 18, WELL, [side * discX, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(browR, browTube, 6, 18, Math.PI),
      color: PAINT,
      position: [side * (discX + 0.015), WHEEL_R, z],
      rotation: [0, Math.PI / 2, 0],
      slot: "paint",
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair flanks the nose intake; rear pair sits beside the tail lamps.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.58, 0.4, 2.05], size: [0.12, 0.05, 0.04] },
  { name: "indicator-fr", pos: [0.58, 0.4, 2.05], size: [0.12, 0.05, 0.04] },
  { name: "indicator-rl", pos: [-0.72, 0.55, -2.02], size: [0.09, 0.06, 0.03] },
  { name: "indicator-rr", pos: [0.72, 0.55, -2.02], size: [0.09, 0.06, 0.03] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear (wide rear track — fat rear tyres at the corners) ----------
  P.push(
    ...wheel(-1, WHEEL_X_F, TYRE_W_F, AXLE_F, "wheel-fl"), ...wheel(1, WHEEL_X_F, TYRE_W_F, AXLE_F, "wheel-fr"),
    ...wheel(-1, WHEEL_X_R, TYRE_W_R, AXLE_R, "wheel-rl"), ...wheel(1, WHEEL_X_R, TYRE_W_R, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2]));
  }
  for (const s of S) {
    P.push(...arch(s, AXLE_F, 0.86, 0.39, 0.37, 0.05)); // front arch
    P.push(...arch(s, AXLE_R, 0.9, 0.43, 0.41, 0.055)); // flared rear arch, clearly wider
  }
  P.push(box(1.65, 0.12, 3.4, TRIM, [0, 0.2, -0.05])); // underbody pan

  // -- Body masses (paint) — a low, wide tub (belt at ~0.58) ------------------
  P.push(paint(1.72, 0.36, 3.3, [0, 0.4, -0.05])); // main tub, sill to belt line

  // Low pointed teardrop nose: a very low front prow face, a bonnet lid diving
  // to it, and rounded fender crowns beside the lid — the whole front reads as
  // one soft, low falling line.
  P.push(paint(1.46, 0.24, 0.16, [0, 0.42, 2.0])); // low front prow face
  P.push(paint(1.34, 0.06, 1.2, [0, 0.53, 1.42], [HOOD_SLOPE, 0, 0])); // bonnet lid diving to the nose
  for (const s of S) P.push(blob([0.14, 0.09, 0.6], [s * 0.74, 0.51, 1.42])); // rounded fender crowns
  P.push(paint(1.55, 0.22, 0.5, [0, 0.32, 1.82])); // lower nose, bridging tub to prow (no under-gap)
  P.push(box(1.6, 0.07, 0.16, TRIM, [0, 0.2, 2.03])); // low front splitter under the prow
  P.push(box(1.55, 0.14, 0.3, CLAD, [0, 0.23, 1.95])); // front valance filling back to the arch

  // -- Rear haunches (the widest masses; rounded hips OUTBOARD of the cabin) ---
  // Quarter-panels over the wide rear wheels with a rounded shoulder blob on
  // top, flaring well past the greenhouse so the car sits planted and low, then
  // tucking back down — never a flat open bed.
  for (const s of S) {
    P.push(paint(0.3, 0.44, 1.5, [s * 0.77, 0.44, -1.1])); // haunch body, outer edge ~0.92
    P.push(blob([0.22, 0.16, 0.72], [s * 0.72, 0.62, -1.05])); // rounded haunch shoulder
  }

  // -- Engine bay block under the sloping cover (never an open deck) ----------
  P.push(box(1.15, 0.28, 1.4, MESH, [0, 0.55, -0.85])); // dark engine bay mass under the cover

  // -- The unbroken low teardrop deck behind the cabin: backlight sail ->
  //    engine cover -> tail deck, each tilted box overlapping the next so the
  //    canopy falls to the tail in ONE low line with NO flat patch, NO tall wall.
  P.push(paint(1.05, 0.36, 0.78, [0, 0.659, -0.502], [-SAIL_ANGLE, 0, 0])); // backlight sail (tapers to buttresses)
  P.push(paint(1.5, 0.3, 0.72, [0, 0.542, -1.166], [-DECK1_ANGLE, 0, 0])); // engine cover deck
  P.push(paint(1.58, 0.26, 0.6, [0, 0.481, -1.735], [-DECK2_ANGLE, 0, 0])); // tail deck, lands low

  // Rear glass lying on the sail, with the gold engine-bay hint just beneath it.
  P.push(box(0.82, 0.03, 0.72, GLASS, [0, 0.845, -0.52], [-SAIL_ANGLE, 0, 0])); // rear window
  P.push(box(0.6, 0.05, 0.2, GOLD, [0, 0.72, -0.86])); // gold-foil engine bay hint under the glass
  // Central mesh intake band + louvres on the engine cover slope (slope 0.161).
  P.push(box(0.78, 0.03, 0.44, MESH, [0, 0.733, -1.05], [-DECK1_ANGLE, 0, 0])); // engine mesh panel
  for (let i = 0; i < 5; i++) {
    const z = -0.98 - 0.1 * i; // from -0.98 back to -1.38
    const y = 0.74 + 0.161 * (z + 0.88) + 0.02; // ride the falling cover slope
    P.push(box(0.9, 0.02, 0.045, MESH, [0, y, z], [-DECK1_ANGLE, 0, 0]));
  }
  // Thin deployable spoiler lip on the tail — the F1's only rear aero, NOT a wing.
  P.push(box(1.4, 0.045, 0.16, TRIM, [0, 0.6, -1.9]));

  // -- Greenhouse (a SMALL, LOW, rounded central bubble; hard-raked screen) ----
  P.push(box(1.1, 0.6, 0.05, SCREEN, [0, 0.75, 0.625], [-SCREEN_RAKE, 0, 0])); // hard-raked windscreen
  for (const s of S) P.push(paint(0.09, 0.58, 0.09, [s * 0.48, 0.75, 0.625], [-SCREEN_RAKE, 0, 0])); // A-pillars
  P.push(paint(1.0, 0.05, 0.6, [0, 0.9, 0.08])); // small low roof, edges overlap screen + sail
  P.push(box(0.95, 0.34, 0.85, CABIN, [0, 0.72, 0.12])); // dark interior block sealing the cabin
  for (const s of S) {
    P.push(box(0.03, 0.24, 0.56, GLASS, [s * 0.48, 0.74, 0.08], [0, 0, s * 0.1])); // side glass, small tumblehome
    P.push(box(0.05, 0.24, 0.07, TRIM, [s * 0.48, 0.74, -0.16], [0, 0, s * 0.1])); // C-pillar closing the bubble
  }
  // A small solid roof intake snorkel sunk into the rear roof edge (the F1 cue),
  // low enough not to break the low bubble.
  P.push(box(0.16, 0.05, 0.2, TRIM, [0, 0.905, -0.14]));

  // -- Flank intake scoops on the haunches (ahead of the rear wheels) --------
  for (const s of S) {
    P.push(box(0.05, 0.22, 0.44, MESH, [s * 0.9, 0.46, -0.6])); // big side intake mouth
    P.push(paint(0.06, 0.05, 0.5, [s * 0.88, 0.58, -0.6])); // scoop upper lip
    P.push(paint(0.06, 0.05, 0.5, [s * 0.88, 0.35, -0.6])); // scoop lower lip
    P.push(paint(0.06, 0.24, 0.06, [s * 0.88, 0.46, -0.38])); // scoop front wall
    P.push(box(0.03, 0.1, 2.7, CLAD, [s * 0.87, 0.26, -0.05])); // rocker sill (reaches both arches)
  }

  // -- Front fascia: slim headlamps on the prow, small intake, low splitter ---
  for (const s of S) {
    P.push(box(0.34, 0.11, 0.05, TRIM, [s * 0.56, 0.5, 2.02])); // headlamp surround
    P.push(box(0.3, 0.08, 0.04, LAMP, [s * 0.56, 0.5, 2.05])); // slim rectangular headlamp
    P.push(box(0.26, 0.02, 0.012, "#8fa5b2", [s * 0.56, 0.535, 2.075])); // upper light strip
  }
  P.push(box(0.66, 0.13, 0.05, MESH, [0, 0.32, 2.03])); // small central intake, no big grille
  for (let i = -1; i <= 1; i++) {
    P.push(box(0.02, 0.11, 0.02, "#26282c", [i * 0.18, 0.32, 2.05])); // intake slats
  }
  P.push(cyl(0.045, 0.045, 0.02, 16, CHROME, [0, 0.46, 2.09], [Math.PI / 2, 0, 0])); // nose badge disc
  P.push(box(0.4, 0.09, 0.02, PLATE, [0, 0.28, 2.06])); // small low front plate

  // -- Rear fascia: quad round tail lamps, mesh grille, dark exhausts ---------
  P.push(box(1.5, 0.34, 0.08, TRIM, [0, 0.46, -2.0])); // black rear panel
  for (const s of S) {
    // two round tail lamps per side on the black panel
    P.push(cyl(0.075, 0.075, 0.05, 16, TAIL_RED, [s * 0.42, 0.52, -2.05], [Math.PI / 2, 0, 0]));
    P.push(cyl(0.075, 0.075, 0.05, 16, TAIL_RED, [s * 0.6, 0.52, -2.05], [Math.PI / 2, 0, 0]));
  }
  // Central black mesh grille with vertical slats.
  P.push(box(0.56, 0.2, 0.03, MESH, [0, 0.48, -2.04]));
  for (let i = -2; i <= 2; i++) {
    P.push(box(0.02, 0.16, 0.02, "#26282c", [i * 0.12, 0.48, -2.06]));
  }
  // A single row of three dark gunmetal exhaust tips, centred low.
  for (const dx of [-0.12, 0, 0.12]) {
    P.push(cyl(0.05, 0.05, 0.09, 12, EXHAUST, [dx, 0.3, -2.06], [Math.PI / 2, 0, 0]));
  }
  P.push(box(1.4, 0.14, 0.1, CLAD, [0, 0.2, -2.02])); // rear diffuser valance
  P.push(box(0.4, 0.09, 0.02, PLATE, [-0.4, 0.38, -2.06])); // small rear plate, offset to one side

  // -- Furniture: small mirrors on the door tops, subtle door cut lines -------
  for (const s of S) {
    P.push(box(0.12, 0.03, 0.04, TRIM, [s * 0.63, 0.6, 0.4])); // mirror stalk overlapping the body
    P.push(paint(0.06, 0.09, 0.09, [s * 0.72, 0.62, 0.4])); // mirror head
    P.push(box(0.012, 0.07, 0.07, GLASS, [s * 0.755, 0.62, 0.41])); // mirror glass, facing back
    P.push(box(0.006, 0.02, 0.5, TRIM, [s * 0.862, 0.55, 0.35], [0.5, 0, 0])); // dihedral door cut line
    P.push(box(0.02, 0.025, 0.11, CHROME, [s * 0.85, 0.5, -0.1])); // door handle
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X_R, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X_R, WHEEL_R, AXLE_R] },
];

export const mclarenF1 = defineModel({
  id: "mclaren-f1",
  name: "McLaren F1",
  batchId: "cars",
  tags: ["car", "mclaren", "f1", "supercar", "sports", "classic"],
  summary: "A 1992 McLaren F1 mid-engine road supercar at true scale, in McLaren Orange.",
  description:
    "A 1992 McLaren F1 at real 1 unit = 1 metre scale (about 4.29 long, 1.82 " +
    "wide, a very low 1.14 tall, 2.72 wheelbase). The silhouette is one " +
    "continuous low, wide teardrop, smoother and rounder than an F40 and with " +
    "no big wing: a low pointed nose with slim headlamps and a small intake, a " +
    "hard-raked windscreen over a small low central canopy (the F1's central " +
    "driving position), and a low rounded engine deck sloping in one unbroken " +
    "line down to a short tail. The car reads strikingly low and wide — the " +
    "canopy barely clears the shoulders and the rounded rear haunches flare " +
    "well outboard of the greenhouse over a wide rear track, fed by big flank " +
    "intake scoops ahead of the rear wheels. The engine cover carries mesh " +
    "intakes, louvres and a gold engine-bay hint under the rear glass, and the " +
    "only rear aero is a thin deployable spoiler lip, not a wing. Quad round " +
    "tail lamps sit on a black rear panel over a mesh grille and triple central " +
    "exhausts, on bright multi-spoke alloys with low-profile tyres. Body panels " +
    "carry the 'paint' recolour slot (McLaren Orange); `drive` spins the wheels " +
    "and `hazards` blinks the four indicators.",
  author: "Fable 5",
  footprint: 2.3,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
